import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { get } from 'lodash';
import { StaticImage } from 'gatsby-plugin-image';
import slugify from 'slugify';
import styled from 'styled-components';
import Layout from '~components/Layout';
import Topper from '~components/base/Topper';
import BodyElement from '~components/body/BodyElement';
import ArchieElement from '~components/body/ArchieElement';
import Byline from '~components/base/Bylines';
import SeoSocial from '~components/base/SeoSocial';
import useBusiness from '~lib/useBusiness';
import GlobalStyles from '../styles/GlobalStyles';
import B150Bug from '~components/logos/B150Bug';

const HeroContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 20px;
  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    grid-column-gap: 20px;
  }
`;

const ImageContainer = styled.div`
  display: block;
`;

export const pageQuery = graphql`
    query {
        site {
          siteMetadata {
            title
            sectionName
            projectName
          }
        }
        story: arcStory(storyId: {eq: "RXQN4S6ZGNFCTPXX33OMJNP4KQ"}) {
            headlines {
                basic
            }
            subheadlines {
                basic
            }
            bodyElements {
              _id
              type
              subtype
              alignment
              content
              archieJson
              url
              srcSet
              caption
              subtype
              bodyElements {
                _id
                content
              }
              citation {
                content
              }
            }
            # getting seo social data
            promoImage {
              url,
              srcSet,
              caption,
              credit {
                name
              }
            }
            description {
              basic
            }
            # getting teasers information
            relatedLinks {
              headlines {
                basic
              }
              promoImage {
                srcSet
                url
              }
              url
            }
            slug
        }
    }
`;

const ArticleTemplate = ({
  data: {
    story,
    site: {
      pathPrefix,
      siteMetadata: { sectionName, projectName },
    },
  },
}) => {
  const slugifiedSection = slugify(sectionName, { lower: true });
  const authorNames = get(story, 'credits', [])
    .map((credit) => credit.name)
    .join(', ');

  useBusiness({
    pageName: `${slugifiedSection} | ${get(story, 'headlines.basic')}`,
    environment: process.env.NODE_ENV,
    meterContent: false,
    pageId: `apps.${story.slug}`,
    section: slugifiedSection,
    author: authorNames,
    projectName,
  });

  const subhed = get(story, 'subheadlines.basic')
    ? (<h3 className="subheadline miller-n4">{get(story, 'subheadlines.basic')}</h3>)
    : (null);
  const metaUrl = `https://apps.bostonglobe.com${pathPrefix}`;

  // testing for presence of publishDate archie element
  // if it exists, set the pubDateObj to it's value and pass to the Byline component below
  // if it doesn't pass null
  let pubDateObj = null;
  story.bodyElements.forEach((e) => {
    if (e.archieJson) {
      const parsedArchie = JSON.parse(e.archieJson);
      if (parsedArchie.publishDate !== undefined) {
        pubDateObj = parsedArchie;
      }
    }
  });

  return (
    <Layout>
      <SeoSocial
        url={metaUrl}
        description={get(story, 'description.basic')}
        title={get(story, 'headlines.basic')}
        imageUrl={get(story, 'promoImage.sizes._1024')}
      />
      <GlobalStyles />
      <Topper>
        <HeroContainer>
          <ImageContainer>
            <StaticImage
              className="hero-image"
              src="../assets/images/app-bunker-hill-engraving.jpg"
              alt="An engraving of Boston Hill from the viewpoint of Bunker Hill done in 1848."
            />
          </ImageContainer>

          <ImageContainer>
            <StaticImage
              className="hero-image"
              src="../assets/images/app-bunker-hill-pano.jpg"
              alt="A panoramic of Boston Hill from the viewpoint of Bunker Hill taken in 2022."
            />
          </ImageContainer>
        </HeroContainer>

        <B150Bug />
        <h1 className="headline millerbanner-n4">{get(story, 'headlines.basic')}</h1>
        {subhed}
        <Byline
          pubDate={pubDateObj}
        />
      </Topper>
      <main>
        {story.bodyElements.map((element) => {
          if (element.archieJson) {
            return (
              <ArchieElement
                data={element}
                key={element._id}
                relatedLinks={get(story, 'relatedLinks')}
              />
            );
          }
          return (
            <BodyElement
              data={element}
              key={element._id}
            />
          );
        })}
      </main>
    </Layout>
  );
};

ArticleTemplate.propTypes = {
  data: PropTypes.shape({
    story: PropTypes.shape({
      bodyElements: PropTypes.arrayOf(
        PropTypes.shape({
          archieJson: PropTypes.string,
        }),
      ),
      slug: PropTypes.string,
    }),
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({
        title: PropTypes.string.isRequired,
        sectionName: PropTypes.string.isRequired,
        projectName: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  pageContext: PropTypes.shape({
    storyId: PropTypes.string,
  }).isRequired,
};

export default ArticleTemplate;
